import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
    Button,
    Typography,
    TextField,
    Tooltip,
    Card,
    CardContent,
    MenuList,
    MenuItem,
    ListItemText,
    TablePagination,
    CircularProgress
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import ModalComponent from '../../Modal/Modal';
import SearchInput from 'components/SearchInput/SearchInput';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import SubdirectoryArrowRightIcon from '@material-ui/icons/SubdirectoryArrowRight';
import { useLocation } from 'react-router-dom';
import { useLanguageSettings } from 'views/Settings';
import { actions as copyModalActions } from 'store/copyTemplateModal';
import { actions as templateActions } from 'store/template';
import { actions as attachmentTemplateActions } from 'store/attachmentTemplate';
import { actions as surveyTemplateActions } from 'store/surveyTemplate';
import { actions as surveyActions } from 'store/surveys';
import { actions as conditionQueryTemplateActions } from 'store/conditionQueryTemplate';
import { actions as recipientQueryTemplateActions } from 'store/recipientQueryTemplate';
import { SelectInput } from 'components';
import Skeleton from 'react-loading-skeleton';
import {
    getEventTypeOptions
} from 'helpers/templateHelper';
import { QueryBuilder } from 'components';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';
import 'survey-core/defaultV2.min.css';

const useStyles = makeStyles(theme => ({
    previewPlaceholder: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px dashed',
        borderColor: theme.palette.divider,
        borderRadius: theme.shape.borderRadius,
        color: theme.palette.text.disabled,
        fontSize: '1.2rem',
        background: theme.palette.background.paper,
    },
    verticalDivider: {
        width: '1px',
        backgroundColor: theme.palette.divider,
        margin: `0 ${theme.spacing(2)}px`,
        alignSelf: 'stretch',
        minHeight: '320px',
    },
    importButton: {
        marginRight: '30px',
        marginBottom: '12px'
    },
    item: {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        width: '200px'
    },
    hidden: {
        visibility: 'hidden'
    },
    visible: {
        color: 'grey'
    },
    leftPanel: {
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-end',
        paddingRight: theme.spacing(3),
        minWidth: '260px'
    },
    rightPanel: {
        flex: 2,
        display: 'flex',
        justifyContent: 'flex-start',
        minWidth: '400px',
        maxWidth: '100%',
        paddingLeft: theme.spacing(2)
    },
    productTypeSelect: {
        width: '100% !important',
        maxWidth: '320px',
    },
    productTypeMenuPaper: {
        width: '320px',
        maxWidth: '320px',
        overflowX: 'hidden',
        '& .MuiMenuItem-root': {
            maxWidth: '100%'
        },
        '& .MuiListItemText-root': {
            maxWidth: '100%'
        },
        '& .MuiListItemText-primary': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        }
    },
    inputSpacing: {
        marginTop: '16px',
        width: '100%',
        maxWidth: '320px',
    },
    iFrame: {
        width: '100%',
        height: '100%',
        border: '0px',
        marginTop: 0,
        boxShadow: theme.shadows[2],
        borderRadius: theme.shape.borderRadius
    },
    templateListItem: {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        maxWidth: '250px',
        '& .MuiListItemText-primary': {
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
        }
    },
    queryPreviewContainer: {
        width: '100%',
        height: '100%',
        border: '1px solid #eee',
        borderRadius: '4px',
        padding: '16px',
        background: '#fafafa',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
    },
    queryPreviewInner: {
        padding: '16px',
        background: 'white',
        borderRadius: '4px',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
    },
    queryPreviewContent: {
        flex: 1,
        overflowY: 'auto',
        minHeight: 0
    },
    surveyPreviewContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
    },
    surveyScrollableContent: {
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        background: '#f5f5f5'
    }
}));

const TEMPLATE_CONFIGS = {
    'templates': {
        pathPattern: '/templates',
        detailsRoute: 'templates/details',
        storeKey: 'template',
        actions: templateActions,
        getMinimalAction: 'getMinimalTemplates',
        getTemplateAction: 'getTemplate',
        previewMode: 'html',
        title: 'Import Template',
        subtitle: 'Select Template'
    },
    'attachment-templates': {
        pathPattern: '/attachment-templates',
        detailsRoute: 'attachment-templates/details',
        storeKey: 'attachmentTemplate',
        actions: attachmentTemplateActions,
        getMinimalAction: 'getMinimalAttachmentTemplates',
        getTemplateAction: 'getAttachmentTemplate',
        previewMode: 'html',
        title: 'Import Template',
        subtitle: 'Select Template'
    },
    'survey-templates': {
        pathPattern: '/surveyResultResponse-templates',
        detailsRoute: 'surveyResultResponse-templates/details',
        storeKey: 'surveyTemplate',
        actions: surveyTemplateActions,
        getMinimalAction: 'getMinimalSurveyTemplates',
        getTemplateAction: 'getSurveyTemplate',
        previewMode: 'html',
        title: 'Import Template',
        subtitle: 'Select Template'
    },
    'surveys': {
        pathPattern: '/surveys',
        detailsRoute: 'surveys/details',
        storeKey: 'survey',
        actions: surveyActions,
        getMinimalAction: 'getMinimalSurveys',
        getTemplateAction: 'getSurvey',
        previewMode: 'survey',
        title: 'Import Survey',
        subtitle: 'Select Survey'
    },
    'condition-queries': {
        pathPattern: '/condition-queries',
        detailsRoute: 'condition-queries/details',
        storeKey: 'conditionQueryTemplate',
        actions: conditionQueryTemplateActions,
        getMinimalAction: 'getMinimalConditionQueryTemplates',
        getTemplateAction: 'getConditionQueryTemplate',
        previewMode: 'query',
        title: 'Import Query',
        subtitle: 'Select Query'
    },
    'recipient-queries': {
        pathPattern: '/recipient-queries',
        detailsRoute: 'recipient-queries/details',
        storeKey: 'queryTemplate',
        actions: recipientQueryTemplateActions,
        getMinimalAction: 'getMinimalQueryTemplates',
        getTemplateAction: 'getQueryTemplate',
        previewMode: 'query',
        title: 'Import Query',
        subtitle: 'Select Query'
    }
};

const getTemplateConfig = (pathname) => {
    return Object.entries(TEMPLATE_CONFIGS).find(([key, config]) => 
        pathname.includes(config.pathPattern)
    )?.[1] || TEMPLATE_CONFIGS['templates'];
};

const CopyTemplateModal = props => {
    const [searchTerm, setSearchTerm] = useState('');
    const { isOpen, closeModal } = props;
    const classes = useStyles();
    const dispatch = useDispatch();
    const location = useLocation();
    
    const templateConfig = getTemplateConfig(location.pathname);
    
    const selectedContext = useSelector(store => store.user.selectedBusinessContext);
    const selectedTenant = useSelector(store => store.user.selectedTenant || {});
    const copyModalState = useSelector(store => store.copyTemplateModal);
    const contextDetails = useSelector(store => store.user.contextDetails || []);
    const validContexts = useSelector(store => store.user.validContexts || []);
    const { languageContent, getLanguageContent } = useLanguageSettings();
    const copyModalSupportedEventTypes = copyModalState.supportedEventTypes;
    const isLoadingSupportedEventTypes = copyModalSupportedEventTypes.isLoading;
    
    const minimalTemplates = useSelector(store => store[templateConfig.storeKey].minimalList);
    const isLoadingMinimalTemplates = useSelector(store => store[templateConfig.storeKey].gettingMinimal);
    const minimalTotalRecords = useSelector(store => store[templateConfig.storeKey].minimalTotalRecords);
    const currentTemplate = useSelector(store => store[templateConfig.storeKey].current);
    const isLoadingTemplate = useSelector(store => store[templateConfig.storeKey].getting);
    const allEventTypeCategoryOptions = useSelector(store => store.events.supportedEventTypes.allEventTypeCategoryOptions);

    const [propertyValue, setPropertyValue] = useState(null);
    const [productType, setProductType] = useState('');
    const [selectedTemplateIdx, setSelectedTemplateIdx] = useState(null);
    const [selectedTemplateDetails, setSelectedTemplateDetails] = useState(null);
    const [page, setPage] = useState(0);
    const [filteredAllEventTypeCategoryOptions, setFilteredAllEventTypeCategoryOptions] = useState([]);
    const [topbarContent, setTopbarContent] = useState(null);
    
    const rowsPerPage = 10;
    const searchTimeoutRef = useRef(null);
    const lastFetchedTemplateRef = useRef(null);
    const productTypeInputRef = useRef(null);
    const productTypeMenuWidth = Math.round(productTypeInputRef.current?.getBoundingClientRect?.().width || 320);
    const productTypeMenuProps = {
        disablePortal: true,
        getContentAnchorEl: null,
        anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
        },
        transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
        },
        PaperProps: {
            className: classes.productTypeMenuPaper,
            style: {
                maxHeight: 320,
                minWidth: productTypeMenuWidth,
                maxWidth: productTypeMenuWidth,
                width: productTypeMenuWidth
            }
        }
    };

    const paginatedTemplates = minimalTemplates;

    // Get the appropriate options source based on entity type per the strict rules
    const getOptionsSourceForEntity = useCallback(() => {
        const { storeKey } = templateConfig;
        // All (transient + non-transient): templates, attachment-templates, condition-queries
        // Non-transient only: survey-templates, surveys, recipient-queries
        if (storeKey === 'survey' || storeKey === 'surveyTemplate') {
            return copyModalSupportedEventTypes.internalSurveyResponseOptions || [];
        } else if (storeKey === 'queryTemplate') {
            return copyModalSupportedEventTypes.productIntegrationOptions || [];
        }
        // Default to allEventTypeCategoryOptions for templates, attachmentTemplate, conditionQueryTemplate
        return copyModalSupportedEventTypes.allEventTypeCategoryOptions || [];
    }, [templateConfig, copyModalSupportedEventTypes]);

    const userContextRoles = selectedTenant?.contextRoles || [];

    const directAccessibleContextIds = useMemo(() => {
        const hasAccessRole = roles => {
            const roleList = roles || [];
            return roleList.indexOf('ADM_ADMIN') >= 0 ||
                roleList.indexOf('ADM_USER') >= 0 ||
                roleList.indexOf('ADM_ROLE') >= 0;
        };

        return new Set(
            userContextRoles
                .filter(contextRole => hasAccessRole(contextRole?.roles))
                .map(contextRole => `${contextRole?.contextId}`)
        );
    }, [userContextRoles]);

    const accessibleContextIds = useMemo(
        () => new Set((validContexts || []).map(contextId => `${contextId}`)),
        [validContexts]
    );

    const hasContextAccess = useCallback(
        (contextId) => {
            const contextIdAsString = `${contextId}`;

            if (directAccessibleContextIds.size > 0) {
                return directAccessibleContextIds.has(contextIdAsString);
            }

            return accessibleContextIds.has(contextIdAsString);
        },
        [accessibleContextIds, directAccessibleContextIds]
    );

    const propertyOptions = useMemo(() => {
        const allContexts = [
            { businessContextId: 'Default', name: 'ALL', level: 0 },
            ...(contextDetails || [])
        ];

        const uniqueContexts = [];
        const seen = new Set();

        allContexts.forEach(context => {
            const contextId = `${context?.businessContextId}`;
            if (!contextId || seen.has(contextId)) {
                return;
            }
            seen.add(contextId);
            uniqueContexts.push(context);
        });

        return uniqueContexts;
    }, [contextDetails]);

    useEffect(() => {
        if (languageContent) {
            setTopbarContent(getLanguageContent('topbar'));
        }
    }, [languageContent]);

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (currentTemplate && (currentTemplate.uuid || currentTemplate.id)) {
            const templateCopy = JSON.parse(JSON.stringify(currentTemplate));
            setSelectedTemplateDetails(templateCopy);
        }
    }, [currentTemplate]);

    useEffect(() => {
        const optionsSource = getOptionsSourceForEntity();
        if (!optionsSource || !allEventTypeCategoryOptions) {
            setFilteredAllEventTypeCategoryOptions(optionsSource || []);
            return;
        }

        const topbarSchemaIds = new Set(
            allEventTypeCategoryOptions
                .filter(option => option.schemaIdentifier)
                .map(option => option.schemaIdentifier)
        );
        
        // Filter to only show products enabled in BOTH source AND destination context.
        // This applies to both hierarchical sources (templates/surveys) and the flat
        // productIntegrationOptions source used by recipient queries.
        const commonSchemaIds = optionsSource
            .filter(option => option.schemaIdentifier && topbarSchemaIds.has(option.schemaIdentifier))
            .map(option => option.schemaIdentifier);

        if (commonSchemaIds.length === 0) {
            setFilteredAllEventTypeCategoryOptions([]);
            return;
        }

        const categoryMap = new Map();
        const productMap = new Map();
        const eventMap = new Map();

        optionsSource.forEach(option => {
            if (option.type === 'PRODUCT_CATEGORY') {
                categoryMap.set(option.displayName, option);
            } else if (option.type === 'PRODUCT_NAME') {
                productMap.set(option.displayName, option);
            } else if (option.schemaIdentifier) {
                eventMap.set(option.schemaIdentifier, option);
            }
        });

        const categoryProductGroups = new Map();
        commonSchemaIds.forEach(schemaId => {
            const eventOption = eventMap.get(schemaId);
            if (!eventOption) return;

            const { productCategory, productName } = eventOption;
            const categoryOption = categoryMap.get(productCategory);
            const productOption = productMap.get(productName);

            if (categoryOption && productOption) {
                const groupKey = `${productCategory}|${productName}`;
                
                if (!categoryProductGroups.has(groupKey)) {
                    categoryProductGroups.set(groupKey, {
                        categoryOption,
                        productOption,
                        events: []
                    });
                }
                categoryProductGroups.get(groupKey).events.push(eventOption);
            }
        });
        const hierarchicalOptions = [];
        categoryProductGroups.forEach(({ categoryOption, productOption, events }) => {
            hierarchicalOptions.push(categoryOption);
            hierarchicalOptions.push(productOption);
            events.forEach(eventOption => {
                hierarchicalOptions.push(eventOption);
            });
        });
        
        setFilteredAllEventTypeCategoryOptions(hierarchicalOptions);
    }, [templateConfig.storeKey, getOptionsSourceForEntity, allEventTypeCategoryOptions]);

    const resetTemplateSelection = useCallback(() => {
        setPage(0);
        setSelectedTemplateIdx(null);
        setSelectedTemplateDetails(null);
    }, []);

    const handleContextChange = useCallback((event, value) => {
        setPropertyValue(value);
        setProductType('');
        resetTemplateSelection();
        lastFetchedTemplateRef.current = null;

        if (value?.businessContextId) {
            const contextId = value.businessContextId === 'Default' ? 'Default' : value.businessContextId;
            dispatch(copyModalActions.getCopyModalContextData(contextId));
            dispatch(copyModalActions.getSupportedEventTypes({
                businessContextId: value.businessContextId
            }));
        } else {
            setFilteredAllEventTypeCategoryOptions([]);
        }
    }, [dispatch, resetTemplateSelection]);

    const fetchTemplatesWithSearch = useCallback((searchValue = '', pageIndex = 0) => {
        if (propertyValue && productType) {
            const selectedProduct = filteredAllEventTypeCategoryOptions.find(
                option => option.schemaIdentifier === productType
            );
            if (selectedProduct) {
                setSelectedTemplateDetails(null);
                
                const payload = {
                    index: pageIndex,
                    pageSize: rowsPerPage,
                    active: true,
                    requiredFields: ['uuid', 'name'],
                    businessContextId: propertyValue.businessContextId,
                    ...(selectedProduct.productCategory && { 
                        productCategory: [selectedProduct.productCategory] 
                    }),
                    ...(selectedProduct.productName && { 
                        productName: [selectedProduct.productName] 
                    }),
                    ...(selectedProduct.eventTypeCategory && {
                        eventTypeCategory: [selectedProduct.eventTypeCategory]
                    }),
                    ...(searchValue.trim() && { name: searchValue.trim() })
                };
                
                dispatch(templateConfig.actions[templateConfig.getMinimalAction](payload));
            }
        }
    }, [propertyValue, productType, filteredAllEventTypeCategoryOptions, rowsPerPage, dispatch, templateConfig]);

    const handleProductTypeChange = useCallback((event) => {
        const newProductType = event.target.value;
        setProductType(newProductType);
        setSearchTerm('');
        resetTemplateSelection();
        lastFetchedTemplateRef.current = null;
        
        if (propertyValue && newProductType) {
            const selectedProduct = filteredAllEventTypeCategoryOptions.find(
                option => option.schemaIdentifier === newProductType
            );
            if (selectedProduct) {
                const payload = {
                    index: 0,
                    pageSize: rowsPerPage,
                    active: true,
                    requiredFields: templateConfig.storeKey === 'survey' ? ['id', 'name'] : ['uuid', 'name'],
                    businessContextId: propertyValue.businessContextId,
                    ...(selectedProduct.productCategory && { 
                        productCategory: [selectedProduct.productCategory] 
                    }),
                    ...(selectedProduct.productName && { 
                        productName: [selectedProduct.productName] 
                    }),
                    ...(selectedProduct.eventTypeCategory && {
                        eventTypeCategory: [selectedProduct.eventTypeCategory]
                    })
                };
                dispatch(templateConfig.actions[templateConfig.getMinimalAction](payload));
            }
        }
    }, [propertyValue, filteredAllEventTypeCategoryOptions, rowsPerPage, dispatch, resetTemplateSelection, templateConfig]);

    const handleTemplateSelection = useCallback((template, idx) => {
        setSelectedTemplateIdx(idx);
        
        const templateIdentifier = template.uuid || template.id;
        if (lastFetchedTemplateRef.current !== templateIdentifier || !selectedTemplateDetails) {
            setSelectedTemplateDetails(null);
            lastFetchedTemplateRef.current = templateIdentifier;
            const payload = { 
                ...(templateConfig.storeKey === 'survey' 
                    ? { surveyId: template.id } 
                    : { templateUuid: template.uuid }),
                businessContextId: propertyValue.businessContextId,
                force: true,
                skipMetadata: true
            };
            dispatch(templateConfig.actions[templateConfig.getTemplateAction](payload));
        }
    }, [propertyValue, selectedTemplateDetails, dispatch, templateConfig]);

    const handleSearchChange = useCallback((e) => {
        const searchValue = e.target.value;
        setSearchTerm(searchValue);
        
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        setPage(0);
        setSelectedTemplateIdx(null);
        setSelectedTemplateDetails(null);
        
        searchTimeoutRef.current = setTimeout(() => {
            fetchTemplatesWithSearch(searchValue, 0);
        }, 300);
    }, [fetchTemplatesWithSearch]);

    const handleSearchClear = useCallback(() => {
        setSearchTerm('');
        resetTemplateSelection();
        
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        fetchTemplatesWithSearch('', 0);
    }, [fetchTemplatesWithSearch, resetTemplateSelection]);

    const handlePageChange = useCallback((e, newPage) => {
        setPage(newPage);
        setSelectedTemplateIdx(null);
        setSelectedTemplateDetails(null);
        
        fetchTemplatesWithSearch(searchTerm, newPage);
    }, [fetchTemplatesWithSearch, searchTerm]);

    const handleImportClick = useCallback(() => {
        if (selectedTemplateDetails && propertyValue && productType) {
            const identifier = selectedTemplateDetails.uuid || selectedTemplateDetails.id;
            const url = `${templateConfig.detailsRoute}/${identifier}?copy=true&businessContextId=${propertyValue.businessContextId}`;
            window.open(url, '_blank');
            closeModal();
        }
    }, [selectedTemplateDetails, propertyValue, productType, closeModal, templateConfig]);

        const getSanitizedPreviewHtml = useCallback((htmlContent = '') => {
                let sanitizedHtml = `${htmlContent || ''}`;
                sanitizedHtml = sanitizedHtml.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');

                // Keep anchor styling/classes (some templates use anchors styled as buttons),
                // but remove navigation and inline event handlers.
                sanitizedHtml = sanitizedHtml.replace(/<a\b([^>]*)>/gi, (match, attrs) => {
                        const safeAttrs = attrs
                                .replace(/\shref\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
                                .replace(/\starget\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
                                .replace(/\srel\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
                                .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '');

                        return `<a${safeAttrs} tabindex="-1">`;
                });

                return `
                    <!doctype html>
                    <html>
                        <head>
                            <meta charset="utf-8" />
                            <style>
                                a, button, [role="button"], input, select, textarea {
                                    pointer-events: none !important;
                                }
                            </style>
                        </head>
                        <body>${sanitizedHtml}</body>
                    </html>
                `;
        }, []);

    const renderRightPanel = () => {
        const previewMode = templateConfig.previewMode;

        if (previewMode === 'html') {
            // For templates (EMAIL and other types), show HTML content in iframe
            return (
                <div className={classes.rightPanel}>
                    {(isLoadingTemplate && propertyValue && productType) ? (
                        <div className={classes.previewPlaceholder}>
                            <CircularProgress size={32} />
                        </div>
                    ) : (propertyValue && productType && selectedTemplateDetails) ? (
                            <iframe
                                className={classes.iFrame}
                                srcDoc={getSanitizedPreviewHtml(
                                    selectedTemplateDetails?.type === 'EMAIL'
                                        ? selectedTemplateDetails.jsonContent?.html
                                        : selectedTemplateDetails.content
                                )}
                            />
                        ) : (
                            <div className={classes.previewPlaceholder}>
                                HTML Preview
                            </div>
                        )}
                </div>
            );
        } else if (previewMode === 'query') {
            // For queries, show a read-only preview of rules and components
            return (
                <div className={classes.rightPanel}>
                    {(isLoadingTemplate && propertyValue && productType) ? (
                        <div className={classes.previewPlaceholder}>
                            <CircularProgress size={32} />
                        </div>
                    ) : (propertyValue && productType && selectedTemplateDetails) ? (
                        <div className={classes.queryPreviewContainer}>
                            <div className={classes.queryPreviewInner}>
                                <div className={classes.queryPreviewContent}>
                                    {selectedTemplateDetails?.conditionExpression && (
                                        <QueryBuilder
                                            name="conditionExpression"
                                            template={{
                                                thisTemplate: selectedTemplateDetails,
                                                attributes: selectedTemplateDetails.eventMetaData || []
                                            }}
                                            query={selectedTemplateDetails.conditionExpression}
                                            isReadOnly={() => true}
                                            isLoading={false}
                                            required={false}
                                        />
                                    )}
                                    {!selectedTemplateDetails?.conditionExpression && (
                                        <Typography variant="body2" color="textSecondary">
                                            No query rules defined
                                        </Typography>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className={classes.previewPlaceholder}>
                            Query Preview
                        </div>
                    )}
                </div>
            );
        } else if (previewMode === 'survey') {
            // For surveys, show SurveyJS preview in read-only mode
            const isCopyingAcrossProperties = selectedContext?.businessContextId !== propertyValue?.businessContextId;
            
            return (
                <div className={classes.rightPanel}>
                    {(isLoadingTemplate && propertyValue && productType) ? (
                        <div className={classes.previewPlaceholder}>
                            <CircularProgress size={32} />
                        </div>
                    ) : (propertyValue && productType && selectedTemplateDetails) ? (
                        <div className={classes.surveyPreviewContainer}>
                            {isCopyingAcrossProperties && (
                                <Alert severity="info">
                                    Survey actions are property specific and will not be imported across properties.
                                </Alert>
                            )}
                            <div className={classes.surveyScrollableContent}>
                                {(() => {
                                    try {
                                        if (selectedTemplateDetails.details) {
                                            const surveyJson = typeof selectedTemplateDetails.details === 'string' 
                                                ? JSON.parse(selectedTemplateDetails.details) 
                                                : selectedTemplateDetails.details;
                                            
                                            const survey = new Model(surveyJson);
                                            survey.mode = 'display';
                                            survey.questionsOnPageMode = 'singlePage';
                                            
                                            return <Survey model={survey} />;
                                        } else {
                                            return (
                                                <Typography variant="body2" color="textSecondary" align="center">
                                                    No survey details available
                                                </Typography>
                                            );
                                        }
                                    } catch (error) {
                                        return (
                                            <Typography variant="body2" color="error" align="center">
                                                Error loading survey preview: {error.message}
                                            </Typography>
                                        );
                                    }
                                })()}
                            </div>
                        </div>
                    ) : (
                        <div className={classes.previewPlaceholder}>
                            Survey Preview
                        </div>
                    )}
                </div>
            );
        } else {
            // Default fallback for unknown preview modes
            return (
                <div className={classes.rightPanel}>
                    <div className={classes.previewPlaceholder}>
                        Preview not available for this template type
                    </div>
                </div>
            );
        }
    };

    return (
        <ModalComponent
            open={isOpen}
            onClose={closeModal}
            title={<Typography variant="h2">{templateConfig.title}</Typography>}
            maxWidth="md"
            fullWidth
            paperStyle={{ width: '900px', maxWidth: '95%' }}
            actions={
                <Button
                    className={classes.importButton}
                    color="primary"
                    variant="contained"
                    onClick={handleImportClick}
                    disabled={!propertyValue || !productType || paginatedTemplates.length === 0 || isLoadingMinimalTemplates || selectedTemplateIdx === null}
                >
                    Import
                </Button>
            }
        >
            <Card>
                <CardContent>
                    <div style={{ display: 'flex', flexDirection: 'row', height: '500px', alignItems: 'stretch' }}>
                        <>
                            <div className={classes.leftPanel}>
                                <Autocomplete
                                    id="copy-modal-property-select"
                                    size="small"
                                    options={propertyOptions}
                                    value={propertyValue}
                                    onChange={handleContextChange}
                                    getOptionDisabled={option => 
                                        selectedContext.businessContextId === option.businessContextId ||
                                        !hasContextAccess(option.businessContextId)
                                    }
                                    getOptionLabel={option => option.name || option.contextName}
                                    renderOption={option => (
                                        <Tooltip title={option.name || option.contextName}>
                                            <div className={classes.item}>
                                                {Array(option.level).fill(
                                                    <SubdirectoryArrowRightIcon className={classes.hidden} />
                                                )}
                                                {option.name !== 'ALL' && (
                                                    <SubdirectoryArrowRightIcon className={classes.visible} />
                                                )}
                                                <span>
                                                    {option.name || option.contextName}
                                                </span>
                                            </div>
                                        </Tooltip>
                                    )}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label={topbarContent?.propertyDropdownLabel || `Select a Property`}
                                            variant="outlined"
                                        />
                                    )}
                                />

                                {propertyValue && (
                                    <div className={classes.inputSpacing} ref={productTypeInputRef}>
                                        {isLoadingSupportedEventTypes ? (
                                            <Skeleton 
                                                height={40} 
                                                style={{ 
                                                    borderRadius: 4, 
                                                    margin: 0, 
                                                    padding: 0,
                                                    display: 'block',
                                                    lineHeight: 1
                                                }} 
                                            />
                                        ) : (
                                            <SelectInput
                                                className={classes.productTypeSelect}
                                                label="Product type"
                                                name="schemaIdentifier"
                                                fullWidth={true}
                                                size="small"
                                                onChange={handleProductTypeChange}
                                                value={productType}
                                                selectProps={{
                                                    renderValue: option => option,
                                                    MenuProps: productTypeMenuProps
                                                }}
                                                options={getEventTypeOptions(
                                                    filteredAllEventTypeCategoryOptions || [],
                                                    false,
                                                    {}
                                                )}
                                            />
                                        )}
                                    </div>
                                )}
                                {propertyValue && productType && (
                                        <div style={{ marginTop: 24, border: '1px solid #eee', borderRadius: 8, padding: 16, background: '#fafafa', maxWidth: 320 }}>
                                             <Typography variant="subtitle1" style={{ marginBottom: 8 }}>{templateConfig.subtitle}</Typography>
                                            <SearchInput
                                                value={searchTerm}
                                                onChange={handleSearchChange}
                                                onClear={handleSearchClear}
                                                placeholder={templateConfig.previewMode === 'survey' ? 'Search surveys...' : templateConfig.previewMode === 'query' ? 'Search queries...' : 'Search templates...'}
                                                style={{ width: '100%' }}
                                            />
                                            {isLoadingMinimalTemplates ? (
                                                <div style={{ padding: 20, textAlign: 'center' }}>
                                                    <CircularProgress size={24} />
                                                </div>
                                            ) : (
                                                <>
                                                    <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #eee', borderRadius: 4 }}>
                                                        <MenuList className={classes.list} id="template-menu-list">
                                                            {paginatedTemplates.length === 0 ? (
                                                                <MenuItem disabled>
                                                                    <ListItemText primary={
                                                                        templateConfig.previewMode === 'survey' 
                                                                            ? 'No surveys found' 
                                                                            : templateConfig.previewMode === 'query' 
                                                                                ? (templateConfig.storeKey === 'conditionQueryTemplate' ? 'No condition queries found' : 'No recipient queries found')
                                                                                    : 'No templates found'
                                                                    } />
                                                                </MenuItem>
                                                            ) : paginatedTemplates.map((template, idx) => (
                                                                <Tooltip key={template.uuid || template.id || idx + page * rowsPerPage} title={template.name || ''} >
                                                                    <MenuItem
                                                                        selected={selectedTemplateIdx === idx}
                                                                        onClick={() => handleTemplateSelection(template, idx)}
                                                                        className={classes.templateListItem}
                                                                    >
                                                                        <ListItemText 
                                                                            primary={template.name || ''}
                                                                        />
                                                                    </MenuItem>
                                                                </Tooltip>
                                                            ))}
                                                        </MenuList>
                                                    </div>
                                                    <TablePagination
                                                        component="div"
                                                        count={minimalTotalRecords || 0}
                                                        page={page}
                                                        onPageChange={handlePageChange}
                                                        rowsPerPage={rowsPerPage}
                                                        rowsPerPageOptions={[]}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    )}
                            </div>
                            <div className={classes.verticalDivider} />
                            {renderRightPanel()}
                        </>
                    </div>
                </CardContent>
            </Card>
        </ModalComponent>
    );
};

export default CopyTemplateModal;